import { createRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';
import {
  VITAL_SIGN_TYPES,
  VITAL_SIGNS_TAB_OPTIONS,
} from '@/features/patient-360/components/vital-signs/consts';
import {
  PATIENT_360_ROUTES,
  VITAL_SIGNS_LAYOUT_ROUTE_ID,
  VITAL_SIGNS_ROUTES,
} from '@/features/patient-360/consts/routes';
import { patient360ViewRoute } from '@/features/patient-360/patient-360-view-route';
import { sharedSearchParams } from '@/features/patient-360/types/route-schema';
import { beforeLoadRouterGuard } from '@/integrations/tanstack-router/before-load-router-guard';
import { relativeTo } from '@/integrations/tanstack-router/relative-to';

const vitalSignsSearchParams = sharedSearchParams.extend({
  vsTab: z
    .enum([VITAL_SIGNS_TAB_OPTIONS.recent, VITAL_SIGNS_TAB_OPTIONS.custom])
    .optional()
    .default(VITAL_SIGNS_TAB_OPTIONS.recent),
  mrn: z.string().min(1),
});

const vitalSignsLayout = createRoute({
  id: VITAL_SIGNS_LAYOUT_ROUTE_ID,
  getParentRoute: () => patient360ViewRoute,
  beforeLoad: beforeLoadRouterGuard([]),
  validateSearch: zodValidator(vitalSignsSearchParams),
}).lazy(() =>
  import('./components/vital-signs/vital-signs-layout').then(
    ({ Route }) => Route,
  ),
);

const vitalSignsIndexRoute = createRoute({
  getParentRoute: () => vitalSignsLayout,
  path: relativeTo(VITAL_SIGNS_ROUTES.index, PATIENT_360_ROUTES.patientView),
  staticData: {
    parent: PATIENT_360_ROUTES.patientView,
    home: null,
  },
  validateSearch: zodValidator(vitalSignsSearchParams),
}).lazy(() =>
  import('./components/vital-signs/vital-signs-dashboard').then(
    ({ Route }) => Route,
  ),
);

const vitalSignsDetailRoute = createRoute({
  getParentRoute: () => vitalSignsLayout,
  path: relativeTo(VITAL_SIGNS_ROUTES.detail, PATIENT_360_ROUTES.patientView),
  staticData: {
    parent: VITAL_SIGNS_ROUTES.index,
    home: PATIENT_360_ROUTES.patientView,
  },
  validateSearch: zodValidator(vitalSignsSearchParams),
  params: {
    parse: ({ type }) => ({
      type: z
        .enum([
          VITAL_SIGN_TYPES.temperature,
          VITAL_SIGN_TYPES.weight,
          VITAL_SIGN_TYPES['blood-pressure'],
          VITAL_SIGN_TYPES['heart-rate'],
        ])
        .parse(type),
    }),
    stringify: ({ type }) => ({ type }),
  },
}).lazy(() =>
  import('./components/vital-signs/vital-signs-detail').then(
    ({ Route }) => Route,
  ),
);

export const vitalSignsRoutes = vitalSignsLayout.addChildren([
  vitalSignsIndexRoute,
  vitalSignsDetailRoute,
]);
